// // utils/reportExport.js
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

export const exportReportToExcel = (reportData, reportName, fileNamePrefix) => {
  if (!reportData || reportData.length === 0) return;

  // STEP 1: Detect the type of report based on the data keys
  const firstItem = reportData[0];
  let exportData = [];
  let colWidths = [];

  // ─── TYPE A: Scheduled vs Actual Report ──────────────────────────────────
  if (firstItem.hasOwnProperty('scheduledHours') && firstItem.hasOwnProperty('actualHours')) {
    exportData = reportData.map((item, index) => {
      const variance = (item.actualHours || 0) - (item.scheduledHours || 0);
      return {
        '#': index + 1,
        'Date': dayjs(item.date).format('MMM DD, YYYY'),
        'Employee Name': item.employeeName || 'N/A',
        'Employee ID': item.employeeId || 'N/A',
        'Shift Name': item.shiftName || 'Unassigned', 
        'Scheduled (h)': (item.scheduledHours || 0).toFixed(2),
        'Actual (h)': (item.actualHours || 0).toFixed(2),
        'Variance (h)': variance >= 0 ? `+${variance.toFixed(2)}` : `${variance.toFixed(2)}`,
      };
    });
    colWidths = [
      { wch: 5 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, 
      { wch: 15 }, { wch: 13 }, { wch: 13 }, { wch: 14 }
    ];
  } 
  
  // ─── TYPE B: Labor Cost Report ───────────────────────────────────────────
  else if (firstItem.hasOwnProperty('grossPay') && firstItem.hasOwnProperty('netPay')) {
    exportData = reportData.map((item, index) => ({
      '#': index + 1,
      'Employee Name': item.employeeName || 'N/A',
      'Employee ID': item.employeeId || 'N/A',
      'Hourly Rate': `$${(item.hourlyRate || 0).toFixed(2)}`,
      'Reg. Hours': (item.regularHours || 0).toFixed(1),
      'OT Hours': (item.overtimeHours || 0).toFixed(1),
      'Gross Pay': `$${(item.grossPay || 0).toFixed(2)}`,
      'Tax Withheld': `$${(item.taxWithheld || 0).toFixed(2)}`,
      'Net Pay': `$${(item.netPay || 0).toFixed(2)}`,
      'Status': item.status || 'Pending'
    }));
    colWidths = [
      { wch: 5 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, 
      { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
    ];
  } 
  
  // ─── TYPE C: Attendance & Punctuality Report ──────────────────────────────
  else if (firstItem.hasOwnProperty('daysWorked') && firstItem.hasOwnProperty('onTimeRate')) {
    exportData = reportData.map((item, index) => ({
      '#': index + 1,
      'Employee Name': item.employeeName || 'N/A',
      'Employee ID': item.employeeId || 'N/A',
      'Days Worked': item.daysWorked || 0,
      'Days Absent': item.absentDays || 0,
      'Late Arrivals': item.lateDays || 0,
      'Early Departures': item.earlyDepartures || 0,
      'Missed Clock-Outs': item.missedClockOuts || 0,
      'On-Time Rate': `${(item.onTimeRate || 0)}%`
    }));
    colWidths = [
      { wch: 5 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, 
      { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 18 }, { wch: 12 }
    ];
  }

  // ─── TYPE D: Overtime Analysis Report ──────────────────────────────────────
  else if (firstItem.hasOwnProperty('totalOvertimeHours') && firstItem.hasOwnProperty('overtimeWorkedDays')) {
    exportData = reportData.map((item, index) => {
      // Recalculate the percentage exactly like the frontend for accuracy
      const otPercent = item.totalWorkedDays > 0 
        ? ((item.overtimeWorkedDays / item.totalWorkedDays) * 100).toFixed(1) 
        : '0.0';

      return {
        '#': index + 1,
        'Employee Name': item.employeeName || 'N/A',
        'Employee ID': item.employeeId || 'N/A',
        'Regular Hrs': (item.regularHours || 0).toFixed(1),
        'OT Hrs': (item.totalOvertimeHours || 0).toFixed(1),
        'OT Rate': `$${(item.overtimeRate || 0).toFixed(2)}/hr`,
        'OT Cost': `$${(item.overtimeCost || 0).toFixed(2)}`,
        'OT Days': item.overtimeWorkedDays || 0,
        'Total Days': item.totalWorkedDays || 0,
        'OT % of Total Days': `${otPercent}%`
      };
    });
    colWidths = [
      { wch: 5 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, 
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }
    ];
  }

  // ─── TYPE E: Employee Hours Summary Report ──────────────────────────────────
  else if (firstItem.hasOwnProperty('daysPresent') && firstItem.hasOwnProperty('totalRegularHours')) {
    exportData = reportData.map((item, index) => {
      const totalHours = (item.totalRegularHours || 0) + (item.totalOvertimeHours || 0);
      const avgPerDay = item.daysPresent > 0 ? (totalHours / item.daysPresent).toFixed(1) : 0;
      return {
        '#': index + 1,
        'Employee Name': item.employeeName || 'N/A',
        'Employee ID': item.employeeId || 'N/A',
        'Days Present': item.daysPresent || 0,
        'Regular Hrs': (item.totalRegularHours || 0).toFixed(1),
        'OT Hrs': (item.totalOvertimeHours || 0).toFixed(1),
        'Total Hrs': totalHours.toFixed(1),
        'Avg Hrs/Day': avgPerDay
      };
    });
    colWidths = [
      { wch: 5 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, 
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
    ];
  } 
  
  // ─── FALLBACK: Generic mapping if report type is unknown ──────────────────
  else {
    exportData = reportData.map((item, index) => {
      const flatRow = { '#': index + 1 };
      Object.keys(item).forEach(key => {
        // Convert date objects to strings, skip internal IDs or objects
        if (key === 'date' || key.includes('Date')) {
          flatRow[key] = dayjs(item[key]).isValid() ? dayjs(item[key]).format('MMM DD, YYYY') : item[key];
        } else if (typeof item[key] !== 'object' || item[key] === null) {
          flatRow[key] = item[key];
        }
      });
      return flatRow;
    });
    colWidths = [{ wch: 5 }];
  }

  // ─── GENERATE EXCEL FILE ───────────────────────────────────────────────────
  const ws = XLSX.utils.json_to_sheet(exportData);
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, reportName || 'Report');

  const fileName = `${fileNamePrefix || 'report'}_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;
  XLSX.writeFile(wb, fileName);
};